import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import { useTranslation } from "react-i18next";
import SidebarLayout from "../components/sidebarLayout";
import { getAllCategorie } from "../APIs/CategoriesAPI";
import { getAllProdotti } from "../APIs/ProductsAPI";
import {
  createVariant,
  deleteVariant,
  getAllVariants,
  setVariantProducts,
  updateVariant,
} from "../APIs/VariantsAPI";

const emptyForm = { id: null, name: "", price: "", description: "" };

const getVariantProductIds = (variant) =>
  (Array.isArray(variant?.products) ? variant.products : [])
    .map((entry) => Number(entry?.productId ?? entry?.product?.id))
    .filter((id) => Number.isInteger(id) && id > 0);

function GestioneVarianti() {
  const { t } = useTranslation();
  const tenant = localStorage.getItem("tenantId");
  const token = localStorage.getItem("accessToken");
  const [variants, setVariants] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [linkedProductIds, setLinkedProductIds] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [saving, setSaving] = useState(false);

  const refreshVariants = useCallback(async () => {
    const data = await getAllVariants(token, tenant);
    setVariants(Array.isArray(data) ? data : []);
  }, [tenant, token]);

  const refreshProducts = useCallback(async () => {
    const data = await getAllProdotti(token, tenant);
    setProducts(Array.isArray(data) ? data : []);
  }, [tenant, token]);

  const refreshCategories = useCallback(async () => {
    const data = await getAllCategorie(token, tenant);
    setCategories(Array.isArray(data) ? data : []);
  }, [tenant, token]);

  useEffect(() => {
    refreshVariants().catch((err) => console.error("Errore nel caricamento varianti:", err));
    refreshProducts().catch((err) => console.error("Errore nel caricamento prodotti:", err));
    refreshCategories().catch((err) => console.error("Errore nel caricamento categorie:", err));
  }, [refreshCategories, refreshProducts, refreshVariants]);

  const sortedProducts = useMemo(() => [...products].sort((left, right) =>
    String(left?.name || "").localeCompare(String(right?.name || ""), "it", { sensitivity: "base" })
  ), [products]);

  const sortedCategories = useMemo(() => [...categories].sort((left, right) =>
    String(left?.name || "").localeCompare(String(right?.name || ""), "it", { sensitivity: "base" })
  ), [categories]);

  const productBelongsToCategory = useCallback((product, selectedCategoryId) => {
    if (!selectedCategoryId) return true;
    const target = Number(selectedCategoryId);
    if (Number(product?.categoryId) === target) return true;
    return (Array.isArray(product?.categories) ? product.categories : [])
      .some((entry) => Number(entry?.categoryId ?? entry?.category?.id) === target);
  }, []);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return sortedProducts.filter((product) =>
      productBelongsToCategory(product, categoryId)
      && (!normalizedSearch || String(product?.name || "").toLowerCase().includes(normalizedSearch))
    );
  }, [categoryId, productBelongsToCategory, search, sortedProducts]);

  const categoryProductIds = useMemo(() => categoryId
    ? sortedProducts.filter((product) => productBelongsToCategory(product, categoryId)).map((product) => Number(product.id))
    : [], [categoryId, productBelongsToCategory, sortedProducts]);

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedVariant(null);
    setLinkedProductIds([]);
    setFormOpen(true);
  };

  const openEdit = (variant) => {
    setForm({
      id: variant.id,
      name: variant.name || "",
      price: String(variant.price ?? ""),
      description: variant.description || "",
    });
    setSelectedVariant(variant);
    setLinkedProductIds(getVariantProductIds(variant));
    setFormOpen(true);
  };

  const openLinks = (variant) => {
    setSelectedVariant(variant);
    setLinkedProductIds(getVariantProductIds(variant));
    setSearch("");
    setCategoryId("");
    setLinkOpen(true);
  };

  const openDelete = (variant) => {
    setSelectedVariant(variant);
    setDeleteOpen(true);
  };

  const closeForm = () => {
    setForm(emptyForm);
    setSelectedVariant(null);
    setLinkedProductIds([]);
    setFormOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        price: Number(String(form.price).replace(",", ".")) || 0,
        description: form.description,
      };
      if (form.id) await updateVariant(form.id, payload, token, tenant);
      else await createVariant(payload, token, tenant);
      closeForm();
      await refreshVariants();
    } catch (err) {
      console.error("Errore nel salvataggio variante:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleProduct = async (productId, checked) => {
    if (!selectedVariant) return;
    const nextProductIds = (() => {
      const current = new Set(linkedProductIds.map(Number));
      if (checked) current.add(Number(productId));
      else current.delete(Number(productId));
      return [...current];
    })();

    setLinkedProductIds(nextProductIds);
    try {
      const updated = await setVariantProducts(selectedVariant.id, nextProductIds, token, tenant);
      setSelectedVariant(updated);
      await refreshVariants();
    } catch (err) {
      console.error("Errore nel collegamento variante:", err);
    }
  };

  const handleCategoryProducts = async (checked) => {
    if (!selectedVariant || !categoryProductIds.length) return;
    const next = new Set(linkedProductIds.map(Number));
    categoryProductIds.forEach((id) => checked ? next.add(id) : next.delete(id));
    const nextProductIds = [...next];

    setSaving(true);
    setLinkedProductIds(nextProductIds);
    try {
      const updated = await setVariantProducts(selectedVariant.id, nextProductIds, token, tenant);
      setSelectedVariant(updated);
      await refreshVariants();
    } catch (err) {
      setLinkedProductIds(getVariantProductIds(selectedVariant));
      console.error("Errore nel collegamento della categoria:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVariant?.id) return;
    setSaving(true);
    try {
      await deleteVariant(selectedVariant.id, token, tenant);
      setDeleteOpen(false);
      setSelectedVariant(null);
      await refreshVariants();
    } catch (err) {
      console.error("Errore eliminazione variante:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container sx={{ mt: { xs: 2, md: 4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} flexDirection={{ xs: "column", sm: "row" }} gap={2} mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={800}>{t("variantManagement.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("variantManagement.subtitle")}
            </Typography>
          </Box>
          <Button variant="contained" onClick={openCreate}>{t("variantManagement.addVariant")}</Button>
        </Box>

        <Paper elevation={2} sx={{ maxWidth: "100%", overflow: "hidden" }}>
          {variants.length === 0 ? (
            <Typography sx={{ p: 2 }}>{t("variantManagement.noVariants")}</Typography>
          ) : (
            <List>
              {variants.map((variant) => {
                const linkedCount = getVariantProductIds(variant).length;
                return (
                  <ListItem
                    key={variant.id}
                    divider
                    sx={{
                      alignItems: { xs: "stretch", sm: "flex-start" },
                      display: "flex",
                      gap: { xs: 1, sm: 1.5 },
                      flexDirection: { xs: "column", sm: "row" },
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 1.5, sm: 1 },
                    }}
                  >
                    <ListItemText
                      primary={variant.name}
                      sx={{ minWidth: 0, my: 0, flex: "1 1 auto" }}
                      primaryTypographyProps={{
                        sx: { overflowWrap: "anywhere", fontWeight: 700 },
                      }}
                      secondaryTypographyProps={{
                        component: "div",
                        sx: { overflowWrap: "anywhere", whiteSpace: "normal" },
                      }}
                      secondary={[
                        t("variantManagement.priceLabel", { price: Number(variant.price || 0).toFixed(2) }),
                        t("variantManagement.linkedProductsCount", { count: linkedCount }),
                        variant.description,
                      ].filter(Boolean).join(" - ")}
                    />
                    <Box
                      display="flex"
                      gap={0.5}
                      justifyContent={{ xs: "flex-end", sm: "flex-start" }}
                      alignItems="center"
                      sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "center" } }}
                    >
                      <IconButton aria-label={t("variantManagement.linkProducts")} onClick={() => openLinks(variant)}>
                        <LinkIcon />
                      </IconButton>
                      <IconButton aria-label={t("variantManagement.editVariant")} onClick={() => openEdit(variant)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton aria-label={t("variantManagement.deleteVariant")} onClick={() => openDelete(variant)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>

        <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
          <DialogTitle>{form.id ? t("variantManagement.editVariant") : t("variantManagement.newVariant")}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t("variantManagement.variantName")}
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t("price")}
              value={form.price}
              onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
              margin="normal"
              inputMode="decimal"
            />
            <TextField
              fullWidth
              label={t("variantManagement.notes")}
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm}>{t("confirmOperation.cancel")}</Button>
            <Button variant="contained" onClick={handleSave} disabled={!form.name.trim() || saving}>{t("confirmOperation.confirm")}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t("variantManagement.linkProductsTitle", { name: selectedVariant?.name || "" })}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              size="small"
              label={t("variantManagement.searchProduct")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              margin="normal"
            />
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="variant-category-label">Categoria</InputLabel>
              <Select
                labelId="variant-category-label"
                label="Categoria"
                value={categoryId}
                onChange={(event) => setCategoryId(event.target.value)}
              >
                <MenuItem value="">Tutte le categorie</MenuItem>
                {sortedCategories.map((category) => (
                  <MenuItem key={category.id} value={String(category.id)}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {categoryId && (
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ my: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleCategoryProducts(true)}
                  disabled={saving || categoryProductIds.length === 0}
                >
                  Associa tutta la categoria ({categoryProductIds.length})
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleCategoryProducts(false)}
                  disabled={saving || categoryProductIds.length === 0}
                >
                  Rimuovi tutta la categoria
                </Button>
              </Box>
            )}
            <List sx={{ maxHeight: 420, overflowY: "auto" }}>
              {filteredProducts.map((product) => {
                const checked = linkedProductIds.map(Number).includes(Number(product.id));
                return (
                  <ListItem key={product.id} divider sx={{ px: { xs: 0, sm: 2 } }}>
                    <Checkbox
                      checked={checked}
                      onChange={(event) => handleToggleProduct(product.id, event.target.checked)}
                    />
                    <ListItemText
                      primary={product.name}
                      secondary={`${product.categoryName || "Senza categoria"} - ${Number(product.price || 0).toFixed(2)} EUR`}
                    />
                  </ListItem>
                );
              })}
              {filteredProducts.length === 0 && (
                <ListItem><ListItemText primary="Nessun articolo trovato" /></ListItem>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setLinkOpen(false)}>{t("variantManagement.close")}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>{t("variantManagement.deleteVariant")}</DialogTitle>
          <DialogContent>
            {t("variantManagement.deleteConfirm", { name: selectedVariant?.name || "" })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>{t("confirmOperation.cancel")}</Button>
            <Button variant="contained" onClick={handleDelete} disabled={saving}>{t("confirmOperation.confirm")}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </SidebarLayout>
  );
}

export default GestioneVarianti;
